import { DatePipe, NgClass, NgFor, NgIf } from '@angular/common';
import { Component, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import { ApiService } from '../../core/api.service';
import { FeedbackService } from '../../shared/feedback.service';

type DayType = 'Working Day' | 'Holiday' | 'Half Day';

interface CalendarDay {
  _id?: string;
  date: string;
  dayType: DayType;
  name?: string;
  notes?: string;
  source: 'default' | 'calendar';
  inMonth?: boolean;
  isToday?: boolean;
  isPast?: boolean;
}

@Component({
  selector: 'app-holiday-calendar',
  standalone: true,
  imports: [FormsModule, DatePipe, NgFor, NgIf, NgClass, MatButtonModule, MatDialogModule, MatIconModule, MatSelectModule],
  templateUrl: './holiday-calendar.component.html',
  styleUrl: './holiday-calendar.component.scss'
})
export class HolidayCalendarComponent implements OnInit {
  @ViewChild('dayDialog') dayDialog?: TemplateRef<unknown>;

  weekdays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  dayTypes: DayType[] = ['Working Day', 'Holiday', 'Half Day'];
  viewDate = new Date();
  days: CalendarDay[] = [];
  selected?: CalendarDay;
  form = { dayType: 'Working Day' as DayType, name: '', notes: '' };
  loading = false;
  saving = false;
  private dialogRef?: MatDialogRef<unknown>;

  constructor(private api: ApiService, private feedback: FeedbackService, private matDialog: MatDialog) {}

  ngOnInit() {
    this.viewDate = new Date(this.viewDate.getFullYear(), this.viewDate.getMonth(), 1);
    this.loadMonth();
  }

  get monthLabel() {
    return new Intl.DateTimeFormat('en-IN', { month: 'long', year: 'numeric' }).format(this.viewDate);
  }

  loadMonth() {
    this.loading = true;
    const start = new Date(this.viewDate.getFullYear(), this.viewDate.getMonth(), 1);
    const end = new Date(this.viewDate.getFullYear(), this.viewDate.getMonth() + 1, 0);

    this.api.get<CalendarDay[]>(`/holidays?startDate=${this.toDateInput(start)}&endDate=${this.toDateInput(end)}`).subscribe({
      next: (rows) => {
        this.days = this.buildCalendarGrid(rows || []);
        this.loading = false;
      },
      error: (error) => {
        this.feedback.error(error.error?.message || 'Unable to load holiday calendar');
        this.loading = false;
      }
    });
  }

  previousMonth() {
    this.viewDate = new Date(this.viewDate.getFullYear(), this.viewDate.getMonth() - 1, 1);
    this.loadMonth();
  }

  nextMonth() {
    this.viewDate = new Date(this.viewDate.getFullYear(), this.viewDate.getMonth() + 1, 1);
    this.loadMonth();
  }

  currentMonth() {
    const now = new Date();
    this.viewDate = new Date(now.getFullYear(), now.getMonth(), 1);
    this.loadMonth();
  }

  openDay(day: CalendarDay) {
    if (!day.inMonth || day.isPast || !this.dayDialog) return;
    this.selected = day;
    this.form = {
      dayType: day.dayType,
      name: day.name || '',
      notes: day.notes || ''
    };
    this.dialogRef = this.matDialog.open(this.dayDialog, {
      width: 'min(440px, calc(100vw - 28px))',
      autoFocus: 'mat-select',
      restoreFocus: true
    });
  }

  saveDay() {
    if (!this.selected) return;
    this.saving = true;
    const payload = {
      date: this.toDateInput(new Date(this.selected.date)),
      dayType: this.form.dayType,
      name: this.form.name,
      notes: this.form.notes,
      active: true
    };
    const request = this.selected._id ? this.api.put(`/holidays/${this.selected._id}`, payload) : this.api.post('/holidays', payload);
    request.subscribe({
      next: () => {
        this.saving = false;
        this.dialogRef?.close();
        this.feedback.success('Calendar day saved');
        this.loadMonth();
      },
      error: (error) => {
        this.saving = false;
        this.feedback.error(error.error?.message || 'Unable to save calendar day');
      }
    });
  }

  dayClass(day: CalendarDay) {
    return {
      muted: !day.inMonth,
      past: day.isPast,
      today: day.isToday,
      working: day.dayType === 'Working Day',
      holiday: day.dayType === 'Holiday',
      half: day.dayType === 'Half Day',
      override: day.source === 'calendar'
    };
  }

  workHours(dayType = this.form.dayType) {
    if (dayType === 'Holiday') return 'No assignments allowed';
    return dayType === 'Half Day' ? '6 working hours' : '9 working hours';
  }

  private buildCalendarGrid(rows: CalendarDay[]) {
    const byDate = new Map(rows.map((row) => [this.toDateInput(new Date(row.date)), row]));
    const first = new Date(this.viewDate.getFullYear(), this.viewDate.getMonth(), 1);
    const last = new Date(this.viewDate.getFullYear(), this.viewDate.getMonth() + 1, 0);
    const gridStart = new Date(first);
    gridStart.setDate(first.getDate() - first.getDay());
    const gridEnd = new Date(last);
    gridEnd.setDate(last.getDate() + (6 - last.getDay()));

    const todayKey = this.toDateInput(new Date());
    const days: CalendarDay[] = [];
    const cursor = new Date(gridStart);
    while (cursor <= gridEnd) {
      const key = this.toDateInput(cursor);
      const row = byDate.get(key);
      const inMonth = cursor.getMonth() === this.viewDate.getMonth();
      days.push({
        _id: row?._id,
        date: row?.date || key,
        dayType: row?.dayType || (cursor.getDay() === 0 ? 'Holiday' : 'Working Day'),
        name: row?.name || '',
        notes: row?.notes || '',
        source: row?.source || 'default',
        inMonth,
        isToday: key === todayKey,
        isPast: key <= todayKey
      });
      cursor.setDate(cursor.getDate() + 1);
    }
    return days;
  }

  private toDateInput(value: Date) {
    const year = value.getFullYear();
    const month = String(value.getMonth() + 1).padStart(2, '0');
    const day = String(value.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
}
